/**
 * #788 CANLI ŞERİT — "şu an sahada kim var".
 *
 * <b>Neden var:</b> talep #788'in son cümlesi şuydu — "Bir kullanıcı GÜNE BAŞLA dediyse, ben
 * BackOffice'deki Ziyaret İzleme ekranında bu güne başlama kaydını GÖREBİLMELİYİM. Şu an
 * göremiyorum." Veri sunucuda vardı ve `GET /api/visit-tracking/live` ucu ÇALIŞIYORDU, ama
 * React panelinde bu ucu çağıran tek bir satır yoktu: açık gün oturumları hiçbir ekranda
 * görünmüyordu. Kokpit sekmesi tarih aralığı + "Listele" istiyor ve GEÇMİŞE bakıyor; canlı
 * durum için ayrı bir okuma gerekiyordu.
 *
 * <b>Neden Saha İzleme sekmesinin üstünde:</b> sekmenin zaten canlı bir ekseni var (otomatik
 * yenileme) ve şerit onun bağlamına oturuyor. Ayrı bir sayfa açmak, aynı soruyu iki yere
 * bölmek olurdu.
 *
 * <b>Filtreden bağımsızdır ve bu bilinçlidir:</b> "şu an açık gün" bir tarih aralığı sorusu
 * değildir. Yalnız distribütör süzgeci uygulanır (kullanıcı zaten onu seçmiştir); yetki
 * daraltması sunucuda `[DataScoped(ScopeAxis.Representative)]` ile yapılır.
 */

import { useCallback, useEffect, useState } from 'react';
import { fetchVt, fmtNum, fmtTime } from './shared';
import { IcRefresh } from './icons';

export function LiveStrip({ vendorId, refreshSec = 60, autoRefresh = true, onPick }) {
  const [items, setItems] = useState(null);
  const [serverTime, setServerTime] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await fetchVt('/live', { vendorId });
      setItems(data?.items || []);
      setServerTime(data?.serverTime || null);
      setError('');
    } catch (e) {
      // Şerit yardımcı bir görünümdür: hata sekmeyi kilitlemez, şeridin içinde yazılır.
      setError(e.message);
    }
  }, [vendorId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(load, Math.max(15, refreshSec) * 1000);
    return () => clearInterval(id);
  }, [autoRefresh, refreshSec, load]);

  return (
    <div className="card vt-live" style={{ marginBottom: 12 }}>
      <div className="sec-h" style={{ padding: '12px 14px 8px', marginBottom: 0 }}>
        <div>
          <b style={{ fontSize: 12.5 }}>Sahada Şu An</b>
          <span className="sub" style={{ display: 'block' }}>
            Güne başlamış (açık oturumu olan) temsilciler · tarih filtresinden bağımsızdır
          </span>
        </div>
        <div style={{ flex: 1 }} />
        {serverTime && (
          <span className="gpill">Sunucu {fmtTime(serverTime, true)}</span>
        )}
        <button type="button" className="chip" onClick={load} title="Canlı listeyi yenile">
          <IcRefresh size={13} />Yenile
        </button>
      </div>

      {error && (
        <div className="srow"><span className="k">Hata</span><span className="v">{error}</span></div>
      )}

      {!error && items === null && (
        <div style={{ padding: 16, color: 'var(--t3)', fontSize: 12.5 }}>Yükleniyor…</div>
      )}

      {!error && items !== null && items.length === 0 && (
        <div style={{ padding: 16, color: 'var(--t3)', fontSize: 12.5 }}>
          Şu anda açık gün oturumu yok — kimse "Güne Başla" dememiş (ya da herkes günü bitirmiş).
        </div>
      )}

      {!error && items !== null && items.length > 0 && (
        <div className="vt-live-grid">
          {items.map((s) => (
            <LiveCard key={s.sessionId} s={s} onPick={onPick} />
          ))}
        </div>
      )}
    </div>
  );
}

function LiveCard({ s, onPick }) {
  // Gerçekleşme: iskelette kaç durak kaldı. `pendingStopCount` null = o oturum "Güne Başla"
  // iskeletinden ÖNCEKİ bir mobil sürümle açılmış → sayı gösterilmez, "—" yazılır.
  const planned = s.plannedPointCount;
  const visited = s.visitedPointCount;

  return (
    <div
      className="vt-live-card"
      role={onPick ? 'button' : undefined}
      tabIndex={onPick ? 0 : undefined}
      onClick={onPick ? () => onPick(s) : undefined}
      onKeyDown={onPick ? (e) => e.key === 'Enter' && onPick(s) : undefined}
      title={onPick ? 'Bu günün ziyaretlerine in' : undefined}
    >
      <div className="hd">
        <span className="dot" />
        <b>{s.representativeName || 'Temsilci'}</b>
        <span style={{ flex: 1 }} />
        <span className="gpill">Başlangıç {fmtTime(s.startedAt)}</span>
      </div>

      <div className="ln">
        <span className="k">Durum</span>
        <span className="v">
          {s.openVisitCustomer
            ? `${s.openVisitCustomer} ziyaretinde (${fmtTime(s.openVisitStartedAt)}'den beri)`
            : 'Ziyaret arasında / yolda'}
        </span>
      </div>

      <div className="ln">
        <span className="k">Rut</span>
        <span className="v">
          {fmtNum(visited)} / {fmtNum(planned)} nokta
          {s.pendingStopCount != null && ` · ${fmtNum(s.pendingStopCount)} durak bekliyor`}
        </span>
      </div>

      <div className="ln">
        <span className="k">Son hareket</span>
        <span className="v">{s.lastActivityAt ? fmtTime(s.lastActivityAt, true) : '—'}</span>
      </div>

      <div className="ln">
        <span className="k">Başlangıç konumu</span>
        <span className="v">
          {s.startLatitude != null && s.startLongitude != null
            ? (
              <a
                href={`https://www.google.com/maps?q=${s.startLatitude},${s.startLongitude}`}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {Number(s.startLatitude).toFixed(5)}, {Number(s.startLongitude).toFixed(5)}
              </a>
            )
            : 'konum alınamamış'}
        </span>
      </div>
    </div>
  );
}
